/**
 * Page Loading Component
 * Centralized loading overlay with progress tracking for all pages
 *
 * Features:
 * - Injects loading overlay HTML into any page
 * - Progress tracking with customizable steps
 * - Smooth animations and transitions
 * - Deduplication to prevent multiple overlays
 * - Global singleton instance management
 */

import { runPageBootstrap } from '../core/page-bootstrap.js';

const BLOCKING_OVERLAY_FLAG = 'data-kfk-blocking-loader';

// ============================================================================
// SPINNER TEMPLATES
// ============================================================================

const SPINNER_TEMPLATES = {
  '1': `<div class="kfk-spinner-dots">
    <div class="kfk-dot"></div><div class="kfk-dot"></div><div class="kfk-dot"></div><div class="kfk-dot"></div>
    <div class="kfk-dot"></div><div class="kfk-dot"></div><div class="kfk-dot"></div><div class="kfk-dot"></div>
  </div>`,
  '2': `<div class="kfk-spinner-bounce">
    <div class="kfk-bounce-dot"></div><div class="kfk-bounce-dot"></div><div class="kfk-bounce-dot"></div>
  </div>`,
  '3': `<div class="kfk-spinner-arc"></div>`,
  '4': `<div class="kfk-spinner-wave">
    <div class="kfk-wave-bar"></div><div class="kfk-wave-bar"></div><div class="kfk-wave-bar"></div>
    <div class="kfk-wave-bar"></div><div class="kfk-wave-bar"></div>
  </div>`,
  '5': `<div class="kfk-spinner-ring">
    <div class="kfk-ring"></div><div class="kfk-ring"></div>
  </div>`,
  '6': `<div class="kfk-spinner-pulse">
    <div class="kfk-pulse-dot"></div><div class="kfk-pulse-dot"></div><div class="kfk-pulse-dot"></div>
  </div>`,
  '7': `<div class="kfk-spinner-grid">
    <div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div>
    <div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div>
    <div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div><div class="kfk-grid-dot"></div>
  </div>`,
  '8': `<div class="kfk-spinner-chase">
    <div class="kfk-chase-dot"></div><div class="kfk-chase-dot"></div>
  </div>`
};

function getSpinnerHTML() {
  const spinnerStyle = localStorage.getItem('kifaka_spinner_style') || '1';
  return SPINNER_TEMPLATES[spinnerStyle] || SPINNER_TEMPLATES['1'];
}

function getSpinnerColor() {
  return localStorage.getItem('kifaka_spinner_color') || '#07A88D';
}

function getColorStyles() {
  const color = getSpinnerColor();
  return `
/* Dynamic Color Styles */
.kfk-dot,
.kfk-bounce-dot,
.kfk-wave-bar,
.kfk-pulse-dot,
.kfk-grid-dot,
.kfk-chase-dot {
  background-color: ${color} !important;
}

.kfk-spinner-arc {
  border-top-color: ${color} !important;
}

.kfk-ring:nth-child(1) {
  border-top-color: ${color} !important;
}

.kfk-ring:nth-child(2) {
  border-top-color: ${color} !important;
  opacity: 0.7;
}
`;
}

// ============================================================================
// HTML TEMPLATE
// ============================================================================

const LOADING_OVERLAY_HTML = `
<div id="initial-loading-overlay" data-kfk-overlay class="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm">
  <div class="flex flex-col items-center gap-4 bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl px-8 py-6 border border-gray-200/50" role="status" aria-live="polite" aria-label="Chargement en cours">
    <!-- Spinner (dynamic based on user preference) -->
    <div class="kfk-spinner" aria-hidden="true">
      ${getSpinnerHTML()}
    </div>
    <!-- Progress text -->
    <div class="text-center space-y-1">
      <p id="loading-step-title" class="text-sm font-medium text-gray-700">Chargement…</p>
      <p id="loading-percentage" class="text-xs text-gray-500">0%</p>
    </div>
  </div>
</div>
`;

// ============================================================================
// CSS INJECTION
// ============================================================================

const LOADING_STYLES = `
/* Page Loading Component Styles */
#initial-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

[data-kfk-overlay] {
  transition: opacity 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out;
}

[data-kfk-overlay].fade-out {
  opacity: 0;
  pointer-events: none;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

/* Loading card container */
#initial-loading-overlay > div {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 1.5rem 2rem;
  border: 1px solid rgba(229, 231, 235, 0.5);
}

.kfk-spinner {
  width: 3.5rem;
  height: 3.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kfk-spinner-dots {
  width: 100%;
  height: 100%;
  position: relative;
  animation: kfk-spin 1.2s linear infinite;
}

.kfk-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform-origin: 0 0;
  opacity: 0;
  animation: kfk-dot-fade 1.2s ease-in-out infinite;
}

.kfk-dot:nth-child(1) {
  transform: translate(-50%, -50%) rotate(0deg) translateY(-24px);
  animation-delay: 0s;
}

.kfk-dot:nth-child(2) {
  transform: translate(-50%, -50%) rotate(45deg) translateY(-24px);
  animation-delay: 0.15s;
}

.kfk-dot:nth-child(3) {
  transform: translate(-50%, -50%) rotate(90deg) translateY(-24px);
  animation-delay: 0.3s;
}

.kfk-dot:nth-child(4) {
  transform: translate(-50%, -50%) rotate(135deg) translateY(-24px);
  animation-delay: 0.45s;
}

.kfk-dot:nth-child(5) {
  transform: translate(-50%, -50%) rotate(180deg) translateY(-24px);
  animation-delay: 0.6s;
}

.kfk-dot:nth-child(6) {
  transform: translate(-50%, -50%) rotate(225deg) translateY(-24px);
  animation-delay: 0.75s;
}

.kfk-dot:nth-child(7) {
  transform: translate(-50%, -50%) rotate(270deg) translateY(-24px);
  animation-delay: 0.9s;
}

.kfk-dot:nth-child(8) {
  transform: translate(-50%, -50%) rotate(315deg) translateY(-24px);
  animation-delay: 1.05s;
}

@keyframes kfk-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes kfk-dot-fade {
  0%, 100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(-24px) scale(0.5);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) rotate(var(--rotation, 0deg)) translateY(-24px) scale(1);
  }
}

/* Spinner 2: Bouncing Dots */
.kfk-spinner-bounce { display: flex; gap: 8px; align-items: center; }
.kfk-bounce-dot { width: 12px; height: 12px; border-radius: 50%; animation: kfk-bounce 1.4s ease-in-out infinite; }
.kfk-bounce-dot:nth-child(1) { animation-delay: 0s; }
.kfk-bounce-dot:nth-child(2) { animation-delay: 0.2s; }
.kfk-bounce-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes kfk-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }

/* Spinner 3: Arc Spinner */
.kfk-spinner-arc { width: 3.5rem; height: 3.5rem; border: 4px solid #E5E7EB; border-radius: 50%; animation: kfk-spin 1s linear infinite; }

/* Spinner 4: Wave Bars */
.kfk-spinner-wave { display: flex; gap: 6px; align-items: center; height: 3rem; }
.kfk-wave-bar { width: 6px; height: 100%; border-radius: 3px; animation: kfk-wave 1.2s ease-in-out infinite; }
.kfk-wave-bar:nth-child(1) { animation-delay: 0s; }
.kfk-wave-bar:nth-child(2) { animation-delay: 0.1s; }
.kfk-wave-bar:nth-child(3) { animation-delay: 0.2s; }
.kfk-wave-bar:nth-child(4) { animation-delay: 0.3s; }
.kfk-wave-bar:nth-child(5) { animation-delay: 0.4s; }
@keyframes kfk-wave { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }

/* Spinner 5: Double Ring */
.kfk-spinner-ring { width: 3.5rem; height: 3.5rem; position: relative; }
.kfk-ring { position: absolute; border: 3px solid transparent; border-radius: 50%; }
.kfk-ring:nth-child(1) { width: 100%; height: 100%; animation: kfk-spin 1.5s linear infinite; }
.kfk-ring:nth-child(2) { width: 70%; height: 70%; top: 15%; left: 15%; animation: kfk-spin-reverse 1s linear infinite; }
@keyframes kfk-spin-reverse { to { transform: rotate(-360deg); } }

/* Spinner 6: Pulse Dots */
.kfk-spinner-pulse { display: flex; gap: 6px; }
.kfk-pulse-dot { width: 10px; height: 10px; border-radius: 50%; animation: kfk-pulse 1.5s ease-in-out infinite; }
.kfk-pulse-dot:nth-child(1) { animation-delay: 0s; }
.kfk-pulse-dot:nth-child(2) { animation-delay: 0.2s; }
.kfk-pulse-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes kfk-pulse { 0%, 100% { transform: scale(0.5); opacity: 0.3; } 50% { transform: scale(1.2); opacity: 1; } }

/* Spinner 7: Grid Pulse */
.kfk-spinner-grid { display: grid; grid-template-columns: repeat(3, 10px); gap: 6px; }
.kfk-grid-dot { width: 10px; height: 10px; border-radius: 2px; animation: kfk-grid-pulse 1.5s ease-in-out infinite; }
.kfk-grid-dot:nth-child(1) { animation-delay: 0s; }
.kfk-grid-dot:nth-child(2) { animation-delay: 0.1s; }
.kfk-grid-dot:nth-child(3) { animation-delay: 0.2s; }
.kfk-grid-dot:nth-child(4) { animation-delay: 0.1s; }
.kfk-grid-dot:nth-child(5) { animation-delay: 0.2s; }
.kfk-grid-dot:nth-child(6) { animation-delay: 0.3s; }
.kfk-grid-dot:nth-child(7) { animation-delay: 0.2s; }
.kfk-grid-dot:nth-child(8) { animation-delay: 0.3s; }
.kfk-grid-dot:nth-child(9) { animation-delay: 0.4s; }
@keyframes kfk-grid-pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } }

/* Spinner 8: Chasing Dots */
.kfk-spinner-chase { width: 3.5rem; height: 3.5rem; position: relative; animation: kfk-spin 2s linear infinite; }
.kfk-chase-dot { position: absolute; width: 12px; height: 12px; border-radius: 50%; top: 0; left: 50%; transform: translateX(-50%); }
.kfk-chase-dot:nth-child(2) { opacity: 0.6; }

#loading-step-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

#loading-percentage {
  font-size: 0.75rem;
  color: #6B7280;
}

.kfk-skeleton-group {
  display: grid;
  gap: 0.45rem;
  width: min(420px, 100%);
  margin-inline: auto;
}

.kfk-skeleton-line {
  height: 0.72rem;
  border-radius: 0.45rem;
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.18) 0%,
    rgba(226, 232, 240, 0.58) 50%,
    rgba(148, 163, 184, 0.18) 100%
  );
  background-size: 220% 100%;
  animation: kfk-skeleton-shimmer 1.25s linear infinite;
}

.kfk-skeleton-line-lg {
  height: 0.95rem;
  width: 68%;
}

@keyframes kfk-skeleton-shimmer {
  0% { background-position: 220% 0; }
  100% { background-position: -220% 0; }
}

.hidden-by-filter {
  display: none !important;
}
`;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Inject CSS styles into the document head
 */
function injectStyles() {
  if (document.getElementById('kfk-loading-styles')) return;

  const style = document.createElement('style');
  style.id = 'kfk-loading-styles';
  style.textContent = LOADING_STYLES + getColorStyles();
  document.head.appendChild(style);
}

/**
 * Inject loading overlay HTML into the document body
 */
function injectOverlay() {
  // Remove any existing overlays first
  dedupeOverlays();

  // Check if overlay already exists
  if (document.getElementById('initial-loading-overlay')) return;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = LOADING_OVERLAY_HTML.trim();
  const overlay = wrapper.firstChild;

  document.body.insertBefore(overlay, document.body.firstChild);
}

function isBlockingOverlayEnabled() {
  if (typeof document === 'undefined') return false;
  const body = document.body;
  if (!body) return false;
  if (!body.hasAttribute(BLOCKING_OVERLAY_FLAG)) return false;
  const raw = String(body.getAttribute(BLOCKING_OVERLAY_FLAG) || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

/**
 * Remove duplicate overlays
 */
function dedupeOverlays() {
  const overlays = Array.from(document.querySelectorAll('#initial-loading-overlay, [data-kfk-overlay]'));
  if (overlays.length > 1) {
    overlays.slice(1).forEach(node => {
      try { node.remove(); } catch (_) {}
    });
  }
}

/**
 * Initialize the loading component (inject HTML and CSS)
 */
function initLoadingComponent() {
  injectStyles();
  if (!isBlockingOverlayEnabled() || shouldSkipOverlayInjection()) {
    removeLoadingOverlay();
    return;
  }
  injectOverlay();
}

/**
 * Skip global initial overlay on admin/internal utility pages.
 */
function shouldSkipOverlayInjection() {
  if (typeof document === 'undefined') return false;
  const body = document.body;
  const path = String(window?.location?.pathname || '').toLowerCase();

  if (body) {
    if (body.classList.contains('kfk-admin-pending')) return true;
    if (body.classList.contains('kfk-internal-pending')) return true;
    const themePage = String(body.getAttribute('data-theme-page') || '').toLowerCase();
    if (themePage === 'admin') return true;
  }

  return (
    path.includes('/admin-theme') ||
    path.includes('/ai-chat-preview') ||
    path.includes('/components/webcomponents/html/page-loading')
  );
}

// ============================================================================
// PROGRESS TRACKING
// ============================================================================

/**
 * No-operation progress object for fallback
 */
const NOOP_PROGRESS = {
  updateProgress() {},
  advance() {},
  complete() {},
  fail() {}
};

/**
 * Create loading progress instance
 * @param {Object} config - Configuration options
 * @param {number} config.totalSteps - Total number of loading steps
 * @param {string} config.initialTitle - Initial loading message
 * @param {Object} config.stepTitles - Map of step numbers to titles
 * @returns {Object} Progress instance
 */
function createLoadingProgress(config) {
  const { totalSteps = 4, initialTitle = 'Chargement...', stepTitles = {} } = config || {};

  let currentStep = 0;
  const circleElement = document.querySelector('.kfk-spinner-arc');
  const titleElement = document.getElementById('loading-step-title');
  const percentageElement = document.getElementById('loading-percentage');

  const maxDashOffset = 125.6;

  /**
   * Update progress to a specific step
   * @param {number} step - Step number (1-based)
   * @param {string} title - Step title (optional)
   */
  function updateProgress(step, title) {
    currentStep = Math.max(0, Math.min(step, totalSteps));
    const percentage = Math.round((currentStep / totalSteps) * 100);
    const dashOffset = maxDashOffset * (1 - currentStep / totalSteps);

    if (circleElement) {
      circleElement.style.strokeDashoffset = dashOffset;
    }

    if (titleElement) {
      titleElement.textContent = title || stepTitles[step] || initialTitle;
    }

    if (percentageElement) {
      percentageElement.textContent = `${percentage}%`;
    }
  }

  /**
   * Advance to the next step
   * @param {string} title - Step title (optional)
   */
  function advance(title) {
    updateProgress(currentStep + 1, title);
  }

  /**
   * Mark loading as complete
   * Automatically hides the overlay after a short delay
   */
  function complete() {
    updateProgress(totalSteps, 'Chargement terminé');

    // Auto-hide overlay after completion
    setTimeout(() => {
      hideInitialLoadingOverlay();
    }, 500);
  }

  /**
   * Mark loading as failed
   * @param {string} message - Error message
   */
  function fail(message = 'Erreur de chargement') {
    if (titleElement) {
      titleElement.textContent = message;
      titleElement.classList.add('text-red-600');
    }
    if (percentageElement) {
      percentageElement.textContent = '';
    }
  }

  return {
    updateProgress,
    advance,
    complete,
    fail
  };
}

/**
 * Build page loading progress instance
 * Wrapper that ensures loading component is initialized
 * @param {Object} config - Configuration options
 * @returns {Object} Progress instance or NOOP_PROGRESS
 */
function buildPageLoadingProgress(config) {
  try {
    initLoadingComponent();
    if (!document.getElementById('initial-loading-overlay')) {
      return NOOP_PROGRESS;
    }
    return createLoadingProgress(config);
  } catch (error) {
    console.error('[page-loading] Failed to build progress:', error);
    return NOOP_PROGRESS;
  }
}

// ============================================================================
// AUTO-INITIALIZATION
// ============================================================================

// Auto-inject the loading component when this module is loaded.
runPageBootstrap('component-page-loading', initLoadingComponent);

// ============================================================================
// OVERLAY CONTROL
// ============================================================================

/**
 * Show the initial loading overlay
 */
function showInitialLoadingOverlay() {
  if (!isBlockingOverlayEnabled()) return;
  initLoadingComponent();
  const overlay = document.getElementById('initial-loading-overlay');
  if (overlay) {
    overlay.classList.remove('hidden', 'fade-out');
  }
}

/**
 * Hide the initial loading overlay with fade animation
 */
function hideInitialLoadingOverlay() {
  const overlay = document.getElementById('initial-loading-overlay');
  if (!overlay) return;

  overlay.classList.add('fade-out');
  setTimeout(() => {
    overlay.classList.add('hidden');
  }, 300);
}

/**
 * Remove the loading overlay from DOM
 */
function removeLoadingOverlay() {
  const overlay = document.getElementById('initial-loading-overlay');
  if (overlay) {
    overlay.remove();
  }
}

/**
 * Show the main loading overlay (for backward compatibility)
 */
function showLoadingOverlay() {
  showInitialLoadingOverlay();
}

/**
 * Hide the main loading overlay (for backward compatibility)
 */
function hideLoadingOverlay() {
  hideInitialLoadingOverlay();
}

// ============================================================================
// UTILITY FUNCTIONS FOR CONTENT LOADING
// ============================================================================

/**
 * Generate spinner SVG HTML
 * @param {string} size - Size class (e.g., 'h-6 w-6')
 * @param {string} color - Color class (e.g., 'text-primary')
 * @returns {string} Spinner SVG HTML
 */
function createSpinnerSVG(size = 'h-6 w-6', color = 'text-primary') {
  return `
    <svg class="animate-spin ${size} ${color} inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
    </svg>
  `;
}

/**
 * Generate table loader row HTML
 * @param {number} colspan - Number of columns to span
 * @param {string} message - Loading message
 * @returns {string} Table row HTML
 */
function tableLoaderRow(colspan, message = 'Chargement...') {
  return `
    <tr>
      <td colspan="${colspan}" class="px-6 py-4 text-sm text-gray-500 text-center">
        <div class="kfk-skeleton-group" data-skeleton="row" aria-label="${message}">
          <div class="kfk-skeleton-line kfk-skeleton-line-lg"></div>
          <div class="kfk-skeleton-line"></div>
        </div>
      </td>
    </tr>
  `;
}

/**
 * Set tbody element to loading state
 * @param {string} id - Element ID
 * @param {number} colspan - Number of columns to span
 * @param {string} message - Loading message
 */
function setTbodyLoading(id, colspan, message = 'Chargement...') {
  const tbody = document.getElementById(id);
  if (tbody) tbody.innerHTML = tableLoaderRow(colspan, message);
}

/**
 * Generate centered loading content HTML
 * @param {string} message - Loading message
 * @param {string} size - Spinner size
 * @returns {string} Loading content HTML
 */
function createLoadingContent(message = 'Chargement...', size = 'h-6 w-6') {
  void size;
  return `
    <div class="py-4" data-skeleton="block" aria-label="${message}">
      <div class="kfk-skeleton-group">
        <div class="kfk-skeleton-line kfk-skeleton-line-lg"></div>
        <div class="kfk-skeleton-line"></div>
      </div>
    </div>
  `;
}

/**
 * Set activities list to loading state
 * @param {string} message - Loading message
 */
function setActivitiesLoading(message = 'Chargement des activités...') {
  const activitiesList = document.getElementById('activitiesList');
  if (activitiesList) activitiesList.innerHTML = createLoadingContent(message);
}

/**
 * Set any element to loading state
 * @param {string} elementId - Element ID
 * @param {string} message - Loading message
 * @param {string} size - Spinner size
 */
function setElementLoading(elementId, message = 'Chargement...', size = 'h-6 w-6') {
  const element = document.getElementById(elementId);
  if (element) element.innerHTML = createLoadingContent(message, size);
}

// ============================================================================
// PAGE-SPECIFIC CONFIGURATIONS
// ============================================================================

/**
 * Dashboard loading configuration
 */
const DASHBOARD_LOADING_CONFIG = {
  totalSteps: 5,
  initialTitle: 'Authentification...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des données principales...',
    3: 'Application des règles UI...',
    4: 'Initialisation des widgets...',
    5: 'Finalisation...'
  }
};

/**
 * Portfolio loading configuration
 */
const PORTFOLIO_LOADING_CONFIG = {
  totalSteps: 4,
  initialTitle: 'Chargement du portefeuille...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des positions...',
    3: 'Calcul des performances...',
    4: 'Finalisation...'
  }
};

/**
 * Graphique loading configuration
 */
const GRAPHIQUE_LOADING_CONFIG = {
  totalSteps: 4,
  initialTitle: 'Chargement des graphiques...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des données de marché...',
    3: 'Initialisation des graphiques...',
    4: 'Finalisation...'
  }
};

/**
 * Transactions loading configuration
 */
const TRANSACTIONS_LOADING_CONFIG = {
  totalSteps: 4,
  initialTitle: 'Chargement des transactions...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des transactions...',
    3: 'Chargement des activités...',
    4: 'Finalisation...'
  }
};

/**
 * Profile loading configuration
 */
const PROFILE_LOADING_CONFIG = {
  totalSteps: 3,
  initialTitle: 'Chargement du profil...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Récupération des informations...',
    3: 'Finalisation...'
  }
};

/**
 * Analysis board loading configuration
 */
const ANALYSEBOARD_LOADING_CONFIG = {
  totalSteps: 4,
  initialTitle: 'Chargement de l\'analyse...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des données de marché...',
    3: 'Analyse des tendances...',
    4: 'Finalisation...'
  }
};

/**
 * Graphique Plus loading configuration
 */
const GRAPHIQUE_PLUS_LOADING_CONFIG = {
  totalSteps: 4,
  initialTitle: 'Chargement des graphiques avancés...',
  stepTitles: {
    1: 'Authentification...',
    2: 'Chargement des données...',
    3: 'Initialisation des graphiques...',
    4: 'Finalisation...'
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
  // Core initialization
  initLoadingComponent,
  dedupeOverlays,

  // Progress tracking
  NOOP_PROGRESS,
  createLoadingProgress,
  buildPageLoadingProgress,

  // Overlay control
  showInitialLoadingOverlay,
  hideInitialLoadingOverlay,
  removeLoadingOverlay,
  showLoadingOverlay,
  hideLoadingOverlay,

  // Content loading utilities
  createSpinnerSVG,
  tableLoaderRow,
  setTbodyLoading,
  createLoadingContent,
  setActivitiesLoading,
  setElementLoading,

  // Page-specific configs
  DASHBOARD_LOADING_CONFIG,
  PORTFOLIO_LOADING_CONFIG,
  GRAPHIQUE_LOADING_CONFIG,
  GRAPHIQUE_PLUS_LOADING_CONFIG,
  TRANSACTIONS_LOADING_CONFIG,
  PROFILE_LOADING_CONFIG,
  ANALYSEBOARD_LOADING_CONFIG
};
